import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";

import {
  Copy,
  Download,
  FileText,
  Video,
  Zap,
  Eye,
  Clock,
  Target,
  Check
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../../firebase.init";

const HookGenerator = () => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState("English");

  // Generated content sections
  const [mainHooks, setMainHooks] = useState([]); 
  const [hooks, setHooks] = useState([]);
  const [visuals, setVisuals] = useState([]);
  const [script, setScript] = useState("");

  // User data
  const [credits, setCredits] = useState(0);
  
  // Tool Configuration
  const [creditCost, setCreditCost] = useState(2);

  // API keys state
  const [apiKeys, setApiKeys] = useState([]);

  // Language options
  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Spanish", label: "Spanish" },
    { value: "French", label: "French" },
    { value: "German", label: "German" },
    { value: "Chinese", label: "Chinese" },
    { value: "Japanese", label: "Japanese" },
    { value: "Hindi", label: "Hindi" },
    { value: "Arabic", label: "العربية (Arabic)" },
    { value: "Portuguese", label: "Português (Portuguese)" },
    { value: "Russian", label: "Русский (Russian)" },
    { value: "Bengali", label: "বাংলা (Bengali)" },
    { value: "Indonesian", label: "Bahasa Indonesia (Indonesian)" },
    { value: "Urdu", label: "اردو (Urdu)" },
    { value: "Italian", label: "Italiano (Italian)" },
    { value: "Turkish", label: "Türkçe (Turkish)" },
    { value: "Korean", label: "한국어 (Korean)" },
    { value: "Vietnamese", label: "Tiếng Việt (Vietnamese)" },
    { value: "Thai", label: "ไทย (Thai)" },
    { value: "Dutch", label: "Nederlands (Dutch)" },
  ];

  // Clean text by removing markdown symbols
  const cleanText = (text) => {
    return text
      .replace(/\*/g, "") 
      .replace(/#{1,6}\s?/g, "") 
      .replace(/`{1,3}/g, "") 
      .replace(/\[.*?\]\(.*?\)/g, "") 
      .replace(/!\[.*?\]\(.*?\)/g, "") 
      .trim();
  };

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List to determine exact Credit Cost
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/tools-section`);
      if (response.data && response.data.success && response.data.data && response.data.data.tools) {
        const hookTool = response.data.data.tools.find(t => t.id === 1); 
        if (hookTool) {
            setCreditCost(hookTool.creditCost);
        }
      }
    } catch (err) {
        console.warn("Could not fetch tools list, using default credit cost");
    } finally {
        setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);
        
        const userResponse = await axios.get(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering
  const constructPrompt = () => {
    return `You are an expert viral video scriptwriter and content strategist. 
    Your task is to create a complete video package based on the topic provided.
    
    Topic: ${topic}
    Language: ${language}
    
    Please generate the following sections:

    1. Main Hooks:
       Generate 3 high-retention YouTube opening hooks.
       - Do NOT use bullet points.
       - Do NOT add explanations.
       - Plain text only.
       - Make hook stand alone and fully copy-ready.
       - Each hook must feel like a powerful video intro narration.
       - Use curiosity gaps, tension, emotional triggers, and open loops.
       - Designed to maximize watch time retention.
       - Format each line strictly as: "HOOK: [Text]"
       - Do not use asterisks (*) or markdown.
       - Each hook must be 30–45 seconds long when spoken (80–130 words).
    
    2. Visual Ideas:
       Provide 4 creative visual scene descriptions or B-roll ideas.
       Format each as a separate line starting with "Scene: ".
    
    3. Full Script:
       Write a complete, engaging script for a short-form video.
       Include spoken dialogue and suggested visual cues in brackets like [Camera zooms in] or [Show graph].
       Keep it concise and high-energy.
    
    Format your response with clear section headers as follows:

    ===MAIN HOOKS===
    HOOK 1: [Main Hook 1]
    HOOK 2: [Main Hook 2]
    HOOK 3: [Main Hook 3]
    
    ===VIRAL HOOKS===
    [Hook 1]
    ...
    
    ===VISUALS===
    Scene: [Visual Idea 1]
    ...
    
    ===SCRIPT===
    [Full Script Content]`;
  };

  // Parse the AI response
  const parseResponse = (responseText) => {
    const sections = responseText.split("===");
    const result = {
      mainHooks: [],
      hooks: [],
      visuals: [],
      script: ""
    };

    for (let i = 1; i < sections.length; i += 2) {
      const sectionName = sections[i] ? sections[i].trim() : "";
      const sectionContent = sections[i + 1] ? sections[i + 1].trim() : "";

      if (sectionName === "MAIN HOOKS") {
        result.mainHooks = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^HOOK:\s*/i, ""))); 
      } else if (sectionName === "VIRAL HOOKS") {
        result.hooks = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^-\s*/, "")));
      } else if (sectionName === "VISUALS") {
        result.visuals = sectionContent
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => cleanText(line.replace(/^Scene:\s*/i, "")));
      } else if (sectionName === "SCRIPT") {
        result.script = cleanText(sectionContent);
      }
    }

    return result;
  };

  // Generate content
  const generateHookContent = async () => {
    if (!topic.trim()) {
      setError("Please enter a topic");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    if (credits < creditCost) {
      setError(
        `Insufficient credits. You need ${creditCost} credits to generate this content.`
      );
      return;
    }

    setGenerating(true);
    setError("");

    // Reset previous results
    setMainHooks([]);
    setHooks([]);
    setVisuals([]);
    setScript("");

    try {
      // Deduct credits
      const deductResponse = await axios.post(
        `https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: creditCost,
        }
      );
      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - creditCost);

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.8,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;
        const parsedContent = parseResponse(generatedText);

        setMainHooks(parsedContent.mainHooks);
        setHooks(parsedContent.hooks);
        setVisuals(parsedContent.visuals);
        setScript(parsedContent.script);

        toast.success("Content generated successfully!");
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      // Refund credits on failure
      try {
        await axios.post(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: creditCost,
        });
        setCredits((prev) => prev + creditCost);
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // Copy helper
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${type} copied!`);
    }).catch(() => {
      toast.error(`Failed to copy ${type.toLowerCase()}`);
    });
  };

  // Download helper
  const downloadContent = () => {
    let allContent = `VIRAL VIDEO PACKAGE\n\n`;
    allContent += `Topic: ${topic}\n\n`;
    
    if(mainHooks.length > 0) {
        allContent += `===MAIN HIGH-RETENTION HOOKS===\n${mainHooks.map((h, i) => `${i+1}. ${h}`).join('\n\n')}\n\n`;
    }
    
    if(visuals.length > 0) {
        allContent += `===VISUAL IDEAS===\n${visuals.join('\n')}\n\n`;
    }
    if(script) {
        allContent += `===FULL SCRIPT===\n${script}\n\n`;
    }

    const blob = new Blob([allContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${topic.replace(/\s+/g, "-")}-hook-package.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Package downloaded!");
  };

  // Helper to render script with visual cues highlighted
  const renderScriptLine = (line) => {
    // Regex to find text within brackets [ ]
    const parts = line.split(/(\[.*?\])/g);
    return parts.map((part, index) => {
      if (part.startsWith('[') && part.endsWith(']')) {
        return <span key={index} style={{ color: '#888', fontStyle: 'italic', fontSize: '0.9em' }}>{part}</span>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const resetForm = () => {
    setTopic("");
    setLanguage("English");
    setMainHooks([]);
    setHooks([]);
    setVisuals([]);
    setScript("");
    setError("");
  };

  const buyMoreCredits = () => {
    window.location.href = "/user/select-package";
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div className="loading-container" style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh'}}>
        <div style={{textAlign:'center'}}>
            <div className="spinner" style={{border:'4px solid #f3f3f3', borderTop:'4px solid #3498db', borderRadius:'50%', width:'40px', height:'40px', animation:'spin 1s linear infinite', margin:'0 auto 10px'}}></div>
            <p>Loading Tool...</p>
        </div>
        <Toaster position="top-right" />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-container" style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', background:'#f9f9f9'}}>
        <div style={{background:'white', padding:'2rem', borderRadius:'8px', boxShadow:'0 4px 6px rgba(0,0,0,0.1)', textAlign:'center'}}>
          <h2>Please log in to use Hook Generate</h2>
          <button onClick={() => (window.location.href = "/login")} style={{padding:'10px 20px', background:'#007bff', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hook-generator-container" style={{ minHeight: "100vh", background: "#f4f6f8", padding: "20px", fontFamily: "sans-serif" }}>
      <Toaster position="top-right" />
      
      {/* Top Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <button onClick={() => window.history.back()} style={{ background: "transparent", border: "1px solid #ccc", padding: "8px 16px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
          ← Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "15px", background: "white", padding: "10px 20px", borderRadius: "30px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <span style={{ fontWeight: "bold", color: "#555" }}>Credits:</span>
          <span style={{ color: credits < 5 ? "#e74c3c" : "#27ae60", fontWeight: "bold", fontSize: "1.1rem" }}>{credits}</span>
          {credits < 5 && (
            <button onClick={buyMoreCredits} style={{ background: "#27ae60", color: "white", border: "none", padding: "5px 12px", borderRadius: "15px", cursor: "pointer", fontSize: "0.8rem" }}>
              Buy More
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div style={{ marginBottom: "30px", textAlign: "center" }}>
        <h1 style={{ fontSize: "2rem", margin: "0", color: "#333", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
          <Zap color="#FFD700" size={32} fill="#FFD700" /> Hook Generate
        </h1>
        <p style={{ color: "#666", marginTop: "10px" }}>
          Transform one topic into high-retention hooks, visual scenes, and a complete script in seconds.
        </p>
      </div>

      {error && (
        <div style={{ background: "#fee", color: "#c33", padding: "15px", borderRadius: "8px", marginBottom: "20px", textAlign: "center", border: "1px solid #fcc" }}>
          {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: "30px", maxWidth: "1400px", margin: "0 auto" }}>
        
        {/* Left Side - Input Form */}
        <div style={{ background: "white", padding: "25px", borderRadius: "12px", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", height: "fit-content" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#444" }}>Input Topic</h3>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#555" }}>Video Topic</label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., How to start a side hustle in 2024, 3 tips for better sleep, The truth about AI..."
              rows={5}
              style={{ width: "100%", padding: "12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "1rem", resize: "vertical", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: "25px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#555" }}>Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{ width: "100%", padding: "12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "1rem", boxSizing: "border-box" }}
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: "15px" }}>
            <button
              onClick={generateHookContent}
              disabled={generating || !topic.trim() || credits < creditCost}
              style={{
                flex: 1,
                background: generating || !topic.trim() || credits < creditCost ? "#ccc" : "#4F46E5",
                color: "white",
                border: "none",
                padding: "14px",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "bold",
                cursor: generating || !topic.trim() || credits < creditCost ? "not-allowed" : "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px"
              }}
            >
              {generating ? <Clock size={18} className="animate-spin" /> : <Zap size={18} />}
              {generating ? "Generating..." : `Generate Package (${creditCost} Credits)`}
            </button>
            <button
              onClick={resetForm}
              style={{
                background: "#f3f4f6",
                color: "#555",
                border: "1px solid #ddd",
                padding: "14px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600"
              }}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Right Side - Output */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          
          {/* Header Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "white", padding: "15px 20px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.03)" }}>
            <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#333" }}>Generated Results</h2>
            <button
              onClick={downloadContent}
              disabled={!script && mainHooks.length === 0}
              style={{
                background: "#10B981",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: (!script && mainHooks.length === 0) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.9rem"
              }}
            >
              <Download size={16} /> Download All
            </button>
          </div>

          {generating ? (
             <div style={{ textAlign: "center", padding: "50px", background: "white", borderRadius: "12px", border: "2px dashed #ddd" }}>
                <div style={{ fontSize: "1.5rem", marginBottom: "15px" }}>⚡</div>
                <p style={{ color: "#666" }}>Analyzing trends and constructing hooks...</p>
             </div>
          ) : (
            <>
              {/* UPDATED: Main Hooks with Numbering and Individual Copy */}
              {mainHooks.length > 0 && (
                <div style={{ background: "white", padding: "20px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", border: "1px solid #ffebe6" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #f0f0f0" }}>
                        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px", color: "#FF4500" }}>
                            <Target size={20} /> Main Hooks
                        </h3>
                        {/* Global copy button removed in favor of individual ones, or keep as fallback */}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                        {mainHooks.map((hook, idx) => (
                            <div key={idx} style={{
                                display: 'flex', 
                                alignItems: 'flex-start',
                                gap: '15px',
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '8px',
                                border: '1px solid #ffccba',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                                transition: '0.2s',
                            }}>
                                {/* Number Badge */}
                                <div style={{
                                    background: '#FF4500', 
                                    color: 'white', 
                                    width: '28px', 
                                    height: '28px', 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    flexShrink: 0,
                                    marginTop: '2px'
                                }}>
                                    {idx + 1}
                                </div>
                                
                                {/* Text Content */}
                                <div style={{ flex: 1, color: '#333', fontWeight: '500', fontSize: '1.05rem', lineHeight: '1.5' }}>
                                    {hook}
                                </div>

                                {/* Individual Copy Button */}
                                <button 
                                    onClick={() => copyToClipboard(hook, `Hook #${idx + 1}`)}
                                    title="Copy this hook"
                                    style={{
                                        background: '#f3f4f6',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        cursor: 'pointer',
                                        color: '#555',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
              )}

            

              {/* Visuals Section */}
              {visuals.length > 0 && (
                <div style={{ background: "white", padding: "20px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", border: "1px solid #eee" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #f0f0f0" }}>
                        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px", color: "#00D2FF" }}>
                            <Eye size={20} /> Visual Ideas
                        </h3>
                        <button onClick={() => copyToClipboard(visuals.join('\n'), "Visuals")} style={{background:'transparent', border:'1px solid #ddd', padding:'4px 8px', borderRadius:'4px', cursor:'pointer'}}>
                            <Copy size={16} />
                        </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
                        {visuals.map((visual, idx) => (
                            <div key={idx} style={{background:'#e6f7ff', padding:'15px', borderRadius:'8px', display:'flex', flexDirection:'column', gap:'8px'}}>
                                <Video size={24} style={{color:'#00D2FF'}} />
                                <p style={{margin:0, fontSize:'0.95rem', color:'#444'}}>{visual}</p>
                            </div>
                        ))}
                    </div>
                </div>
              )}

              {/* UPDATED: Script Section - Professional Display */}
              {script && (
                <div style={{ background: "white", padding: "20px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", border: "1px solid #eee" }}>
                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #f0f0f0" }}>
                        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px", color: "#4ECDC4" }}>
                            <FileText size={20} /> Full Script
                        </h3>
                        <button onClick={() => copyToClipboard(script, "Script")} style={{background:'transparent', border:'1px solid #ddd', padding:'4px 8px', borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px'}}>
                            <Copy size={16} /> Copy Script
                        </button>
                    </div>
                    
                    {/* Professional Script Container */}
                    <div style={{ 
                        background: "#fcfcfc", 
                        padding: "30px", 
                        borderRadius: "4px", 
                        border: "1px solid #e0e0e0",
                        boxShadow: "inset 0 0 10px rgba(0,0,0,0.02)",
                        color: "#333", 
                        fontFamily: "'Courier New', Courier, monospace", // Script font
                        fontSize: "1rem",
                        lineHeight: "1.8",
                        whiteSpace: "pre-wrap"
                    }}>
                        {script.split('\n').map((line, idx) => {
                            if (!line.trim()) return <br key={idx} />;
                            return (
                                <div key={idx} style={{ marginBottom: '8px' }}>
                                    {renderScriptLine(line)}
                                </div>
                            );
                        })}
                    </div>
                </div>
              )}
              
              {!script && mainHooks.length === 0 && !generating && (
                  <div style={{textAlign:'center', padding:'40px', color:'#999', background:'white', borderRadius:'12px'}}>
                      Enter a topic and hit generate to see your results here.
                  </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HookGenerator;